// Primitives 3 — TextField, Select, Textarea.
// Trois champs, un seul socle : label lié par `htmlFor`, message
// d'erreur relié par `aria-describedby`, `aria-invalid` sur l'erreur.

import { useId } from 'react';
import SourceDot from './SourceDot.jsx';

// `hideLabel` masque le libellé À L'ÉCRAN sans le retirer du DOM :
// `sr-only` le laisse au lecteur d'écran. Un champ dont le libellé
// n'existe plus est inutilisable au clavier — c'est la même règle que
// sur NumberField.
function Wrapper({ id, label, hint, error, source, required, hideLabel = false, children }) {
  return (
    <div className="field">
      <label className={hideLabel ? 'field__label sr-only' : 'field__label'} htmlFor={id}>
        {source ? <SourceDot source={source} /> : null}
        {label}
        {required ? <span aria-hidden="true">*</span> : null}
      </label>
      {children}
      {error ? (
        <span className="field__error" id={`${id}-error`}>
          {error}
        </span>
      ) : hint ? (
        <span className="field__hint" id={`${id}-hint`}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

function describedBy(id, error, hint) {
  if (error) return `${id}-error`;
  if (hint) return `${id}-hint`;
  return undefined;
}

export function TextField({
  label, hint, error, source, required, hideLabel = false, id: providedId, ...props
}) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  return (
    <Wrapper
      id={id} label={label} hint={hint} error={error}
      source={source} required={required} hideLabel={hideLabel}
    >
      <input
        id={id}
        className="input"
        type="text"
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy(id, error, hint)}
        required={required}
        {...props}
      />
    </Wrapper>
  );
}

export function Select({
  label, hint, error, source, required, hideLabel = false,
  options = [], id: providedId, children, ...props
}) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  return (
    <Wrapper
      id={id} label={label} hint={hint} error={error}
      source={source} required={required} hideLabel={hideLabel}
    >
      <select
        id={id}
        className="input"
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy(id, error, hint)}
        required={required}
        {...props}
      >
        {children ??
          options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
      </select>
    </Wrapper>
  );
}

export function Textarea({
  label, hint, error, required, rows = 4, hideLabel = false, id: providedId, ...props
}) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  return (
    <Wrapper
      id={id} label={label} hint={hint} error={error} required={required} hideLabel={hideLabel}
    >
      <textarea
        id={id}
        className="input"
        rows={rows}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy(id, error, hint)}
        required={required}
        {...props}
      />
    </Wrapper>
  );
}

export default TextField;
